import { useUnsafeEffect } from '@factorialco/deprecated-design-system'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import i18n from 'modules/core/lib/i18n'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'

import { FinancialWorkspaceResource, HookData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/types/data'
import { FinanceFactSummary, formatData, useFinanceSummaries } from './useFinanceSummaries'
import { useSourcesVisibility } from './useSourcesVisibility'

interface Props {
  skip?: boolean
  resource?: FinancialWorkspaceResource
}

export const useAgreementsData: (props?: Props) => HookData = (props) => {
  const { hasComparisonDate } = useFinancialWorkspace()
  const { canSeeAgreements, canSeeLaborCosts, canSeeEmployeeExpenses } = useSourcesVisibility()

  const skip = !canSeeAgreements || !!props?.skip

  const { data, loading, fetchFinanceSummaries, refetch } = useFinanceSummaries({
    skip: skip,
    resource: props?.resource,
    financeTypes: ['agreement'],
  })

  const {
    data: comparisonData,
    loading: comparisonLoading,
    refetch: comparisonRefetch,
    fetchFinanceSummaries: fetchComparisonFinanceSummaries,
  } = useFinanceSummaries({
    skip: skip,
    compareTo: true,
    resource: props?.resource,
    financeTypes: ['agreement'],
  })

  useUnsafeEffect(() => {
    if (canSeeAgreements && !skip) {
      fetchFinanceSummaries()
      if (hasComparisonDate) fetchComparisonFinanceSummaries()
    }
  }, [
    skip,
    canSeeAgreements,
    hasComparisonDate,
    fetchFinanceSummaries,
    fetchComparisonFinanceSummaries,
  ])

  const onRefetch = useUnsafeCallback(() => {
    refetch()
    if (hasComparisonDate) comparisonRefetch()
  }, [refetch, comparisonRefetch, hasComparisonDate])

  if (!canSeeAgreements) {
    return
  }

  const agreementsData: FinanceFactSummary = data?.[0]
  const agreementComparisonData: FinanceFactSummary = comparisonData?.[0]

  const showSubLabel =
    (!!props?.resource && (canSeeLaborCosts || canSeeEmployeeExpenses)) ||
    (canSeeLaborCosts && canSeeEmployeeExpenses)

  return {
    refetch: onRefetch,
    visible: !!canSeeAgreements,
    loading: loading || (hasComparisonDate && comparisonLoading),
    data: formatData({
      grey: true,
      italic: true,
      data: agreementsData,
      id: `agreements_data`,
      dashed: true,
      label: agreementsData?.label ?? '',
      comparisonData: agreementComparisonData,
      subLabel: showSubLabel
        ? i18n.t('finance.financial_workspace.employee_costs.table.agreement_data.sub_label')
        : undefined,
      loading: loading || (hasComparisonDate && comparisonLoading),
    }),
  }
}
